import { Inject, Injectable } from "@angular/core";
import {
    DEVICE_POLLING_REST_SERVICE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { NextObserver, Observable, Subscriber } from "rxjs";
import { WebSocketSubject } from "rxjs/webSocket";
import { UpdateDetail } from "./nms-auto-updater.component";
import { DevicePollingData, POLLING_PERCENTAGES, PollingFeedback } from "./nms-auto-updater.model";



/**
 * Responsável por gerenciar o polling de múltiplos equipamentos, controlando o
 * início e finalização de todos e armazenando o resultado da operação em conjunto.
 *
 * Esse serviço não é, e não pode ser, singleton pois ele armazena estado conforme o componente
 * que o está utilizando. Os equipamentos monitorados são aqueles configurados no método setup,
 * e a mensagem de feedback montada é de apenas para esses equipamentos. Além disso, o observer
 * e callback de final de polling também são armazenados internamente na instância do serviço.
 */
@Injectable()
export class NmsAutoPollingService {

    private readonly devicesPollingData: Map<number, DevicePollingData>;

    /**
     * Observer configurado quando um componente passa a monitorar o polling
     * Deve ser notificado sempre que o polling requisitado terminar.
     */
    private pollingFinishObserver: Subscriber<any>;

    /**
     * Mensagem exibindo o resumo do polling. Pode ter informações do último polling ou dos pollings em andamento.
     */
    public pollingFeedbackMessage: Array<UpdateDetail>;

    /**
     * Controla quando o polling foi iniciado a partir deste serviço.
     */
    private allDevicesPollingsRequested: boolean;

    /**
     * Função que deve ser chamada para atualizar os dados na finalização do polling.
     */
    private onFinishPollingCallback: () => Observable<any>;

    /**
     * Determina se o websocket deve ser reconectar em caso de falha na conexão.
     */
    private reconnectOnClose: boolean;

    constructor(@Inject(DEVICE_POLLING_REST_SERVICE) private readonly devicePollingRestService: any) {
        this.devicesPollingData = new Map();
        this.reconnectOnClose = true;
        this.allDevicesPollingsRequested = false;
    }

    ngOnDestroy(): void {
        this.disconnect();
    }

    setup(resourceIds: number[], onFinishPollingCallback: () => Observable<any>): void {
        this.onFinishPollingCallback = onFinishPollingCallback;

        resourceIds.forEach(resourceId => {
            const websocket = this.createWebsocket(resourceId);
            const devicePollingData = new DevicePollingData(websocket);

            this.devicesPollingData.set(resourceId, devicePollingData);
        })
    }

    /*
     * Solicita polling para os equipamentos especificados no setup.
     * Esse método deve ser chamado a cada polling requisitado pelo usuário ou auto updater.
     *
     * Retorna um Observable que notificará quando todos os pollings forem finalizados, com sucesso ou erro.
     */
    startDevicesPolling(): Observable<any> {
        this.allDevicesPollingsRequested = true;
        this.devicePollingRestService.requestPollings([...this.devicesPollingData.keys()]);

        return new Observable(pollingFinishObserver => {
            this.pollingFinishObserver = pollingFinishObserver;
        });
    }

    private createWebsocket(resourceId: number): WebSocketSubject<PollingFeedback> {
        const websocketUrl = `wss://${window.location.host}/service/device/notification/${resourceId}`;
        const websocket = this.createWebSocketSubject(websocketUrl, resourceId);

        this.configureWebsocketListener(websocket);

        return websocket;
    }

    private createWebSocketSubject(websocketUrl: string, resourceId: number) {
        return new WebSocketSubject<PollingFeedback>({
            url: websocketUrl,
            closeObserver: this.createCloseObserver(resourceId)
        });
    }

    /**
     * Monitora a conexão websocket e reconecta em caso de falha, conforme a
     * variável {@link reconnectOnClose}.
     */
    private createCloseObserver(resourceId: number): NextObserver<CloseEvent> {
        return {
            next: event => {
                const devicePollingData = this.devicesPollingData.get(resourceId);
                devicePollingData.finished = true;

                if (this.reconnectOnClose) {
                    devicePollingData.websocket = this.createWebsocket(resourceId);
                }
            }
        };
    }

    private configureWebsocketListener(websocket: WebSocketSubject<PollingFeedback>) {
        websocket.subscribe((feedback: PollingFeedback) => {
            if (this.hasPollingFinished(feedback)) {
                const devicePollingData = this.devicesPollingData.get(feedback.resourceId);
                devicePollingData.finished = true;
                devicePollingData.lastFeedback = feedback;

                const allFinished = Array.from(this.devicesPollingData.values()).every(data => data.finished);

                if (allFinished && this.allDevicesPollingsRequested) {
                    this.devicesPollingData.forEach(data => data.finished = false);

                    this.loadData();
                }
            }
        });
    }

    /**
     * Chama a função que carrega os dados da tela e notifica usando
     * o {@link pollingFinishObserver}.
     *
     * Em caso de sucesso, envia os dados carregados pela {@link onFinishPollingCallback}
     * (PaginatedResponse, por exemplo), e em caso de erro, envia o erro ocorrido.
     */
    private loadData() {
        this.onFinishPollingCallback().subscribe(
            (data) => {
                this.pollingFinishObserver.next(data);
            },
            (error) => {
                this.pollingFinishObserver.error(error);
            },
            () => {
                this.allDevicesPollingsRequested = false;
                this.updateFeedbackMessage();
            }
        );
    }

    private hasPollingFinished(feedback: PollingFeedback) {
        return feedback.percentage === POLLING_PERCENTAGES.FINISHED || feedback.percentage === POLLING_PERCENTAGES.ERROR;
    }

    private updateFeedbackMessage() {
        const updateDetails: Array<UpdateDetail> = [];

        this.devicesPollingData.forEach(data => {
            if (data.lastFeedback) {
                updateDetails.push(this.createFeedbackDetails(data.lastFeedback));
            }
        });

        this.pollingFeedbackMessage = updateDetails;
    }

    private createFeedbackDetails(feedback: PollingFeedback): UpdateDetail {
        const title = feedback.payload.name;
        const success = feedback.percentage === POLLING_PERCENTAGES.FINISHED;
        const subtitle = success ? feedback.description : feedback.processDetails;

        return {title, subtitle, success };
    }

    private disconnect(): void {
        this.reconnectOnClose = false;
        this.devicesPollingData.forEach(data => {
            data.websocket.complete();
        });
    }
}
